import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useSpinner } from '@/hooks/useSpinner';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  CreditCard,
  ArrowDown,
  ArrowUp,
  Clock,
  X,
  Receipt,
  Gem,
  Calendar,
  User,
  Package,
  Info
} from 'lucide-react';
import { diamondsService, type ExtratoMovimentacao } from '@/services/diamonds.service';
import { useAuthContext } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '@/components/LoadingSpinner';

const ExtratosDiamantes: React.FC = () => {
  const { isLoading, showSpinner, hideSpinner } = useSpinner();
  const { toast } = useToast();
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [extratos, setExtratos] = useState<ExtratoMovimentacao[]>([]);
  const [showSaibaMaisModal, setShowSaibaMaisModal] = useState(false);

  useEffect(() => {
    if (user?.id) {
      carregarExtratos();
    }
  }, [user]);

  const carregarExtratos = async () => {
    if (!user?.id) return;

    try {
      showSpinner('Aguarde', 'Carregando extratos...');
      const response = await diamondsService.getMovimentacao(user.id);
      setExtratos(response || []);
    } catch (error) {
      console.error('Erro ao carregar extratos:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar extratos. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      hideSpinner();
    }
  };

  const getTipoMovimentacao = (action: string): string => {
    switch (action) {
      case 'credito_venda':
        return 'Diamantes Recebidos';
      case 'credito_bonus':
        return 'Diamantes Bônus Recebidos';
      case 'resgate_pendente':
        return 'Processando Resgate';
      case 'resgate_aprovado':
        return 'Diamantes Resgatados';
      case 'resgate_reprovado':
        return 'Resgate não Completado';
      default:
        return 'Outros';
    }
  };

  const getIconConfig = (action: string) => {
    switch (action) {
      case 'credito_venda':
      case 'credito_bonus':
        return {
          bgColor: 'bg-green-100',
          textColor: 'text-green-600',
          icon: CreditCard,
          arrow: ArrowDown
        };
      case 'resgate_aprovado':
      case 'debito_outros':
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-600',
          icon: CreditCard,
          arrow: ArrowUp
        };
      case 'resgate_pendente':
        return {
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-600',
          icon: Clock,
          arrow: null
        };
      case 'resgate_reprovado':
        return {
          bgColor: 'bg-red-100',
          textColor: 'text-red-600',
          icon: X,
          arrow: null
        };
      default:
        return {
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-600',
          icon: Receipt,
          arrow: null
        };
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    })} ${dateString.split("T")[1].substring(0,5)}`;
  };

  const formatExpirationDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const ExtratoItem = ({ extrato }: { extrato: ExtratoMovimentacao }) => {
    const iconConfig = getIconConfig(extrato.action);
    const IconComponent = iconConfig.icon;
    const ArrowComponent = iconConfig.arrow;

    return (
      <Card className="mb-4 hover:shadow-md transition-shadow">
        <CardContent className="p-6 pt-8">
            <div className='flex justify-end -mt-[10px]'>
                <Badge variant="outline" className="font-mono">
                  {formatDate(extrato.date_created)}
                </Badge>
            </div>
           
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Ícone da Transação */}
              <div className={`relative w-14 h-14 rounded-full ${iconConfig.bgColor} flex items-center justify-center`}>
                <IconComponent className={`w-6 h-6 ${iconConfig.textColor}`} />
                {ArrowComponent && (
                  <ArrowComponent className={`w-4 h-4 ${iconConfig.textColor} absolute -bottom-1 -right-1`} />
                )}
              </div>

              {/* Informações da Transação */}
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-1">                   
                  <h3 className="font-semibold text-gray-900">
                    {getTipoMovimentacao(extrato.action)}
                  </h3>
                  <div className="flex items-center gap-1">
                    <span className="text-lg font-bold text-purple-600">
                      {extrato.quantity}
                    </span>
                    <Gem className="w-4 h-4 text-purple-600" />
                  </div>
                </div>

                <div className="space-y-1 text-sm text-gray-600">
                  {extrato.codepagarme && (
                    <div className="flex items-center gap-2">
                      <Receipt className="w-4 h-4" />
                      <span><strong>Venda</strong> #{extrato.codepagarme}</span>
                    </div>
                  )}

                  {extrato.nome_cliente && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span><strong>Cliente</strong> "{extrato.nome_cliente}"</span>
                    </div>
                  )}

                  {extrato.date_expiration && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span><strong>Expiram em</strong> {formatExpirationDate(extrato.date_expiration)}</span>
                    </div>
                  )}

                  {extrato.item_nome && (
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      <span><strong>Item</strong> "{extrato.item_nome}"</span>
                    </div>
                  )}

                  {extrato.action === 'resgate_pendente' && (
                    <div className="bg-blue-50 p-3 rounded-lg mt-2">
                      <p className="text-xs text-blue-700 italic leading-relaxed">
                        Sua solicitação de resgate está em análise. Em breve, você receberá a confirmação.<br />
                        O item será enviado conforme os termos e regulamento de entregas do Programa de Benefícios.
                      </p>
                    </div>
                  )}

                  {extrato.observation && (
                    <div className="flex items-start gap-2 mt-2">
                      <Info className="w-4 h-4 mt-0.5" />
                      <span className="text-xs"><strong>Obs.:</strong> {extrato.observation}</span>
                    </div>
                  )}

                  {extrato.action === 'resgate_reprovado' && (
                    <div className="bg-red-50 p-3 rounded-lg mt-2">
                      <p className="text-xs text-red-700">
                        <strong>Obs.:</strong> O valor foi reestituído para o saldo
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <LoadingSpinner isLoading={isLoading} />
      
      <div className="max-w-4xl mx-auto">
        {/* Header com Botão Voltar */}
        <div className="mb-6">
          <div className="flex justify-between gap-2 mb-4">
            <Button
              variant="outline"
              onClick={() => navigate('/loja-recompensas')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSaibaMaisModal(true)}
            >
              <Info className="w-4 h-4 mr-2" />
              Saiba Mais
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-purple-600" />
                Extrato de Diamantes
              </CardTitle>
              <p className="text-sm text-gray-600">
                Acompanhe todas as suas movimentações de diamantes
              </p>
            </CardHeader>
          </Card>
        </div>

        {/* Lista de Extratos */}
        {extratos.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600">
                Nenhuma movimentação encontrada. Continue {user?.role == 'cliente' ? 'comprando' : 'vendendo'} e indicando amigos para acumular diamantes e resgatar itens incríveis!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {extratos.map((extrato) => (
              <ExtratoItem key={extrato.id} extrato={extrato} />
            ))}
          </div>
        )}

        {/* Informações Adicionais */}
        {/* {extratos.length > 0 && (
          <Card className="mt-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Info className="w-4 h-4" />
                <span>
                  Mostrando {extratos.length} movimentação(ões). 
                  Os diamantes têm validade de 2 anos a partir da data de recebimento.
                </span>
              </div>
            </CardContent>
          </Card>
        )} */}

        {/* Modal Saiba Mais */}
        <Dialog open={showSaibaMaisModal} onOpenChange={setShowSaibaMaisModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Sobre o Extrato de Diamantes</DialogTitle>
              <DialogDescription>
                Entenda melhor suas movimentações e como funcionam os diamantes
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 text-sm leading-relaxed">
              {/* 1. O que é o extrato de diamantes? */}
              <div>
                <h3 className="font-semibold text-base mb-2">1. O que é o extrato de diamantes?</h3>
                <p className="text-gray-700">
                  É o histórico de todas as movimentações do seu saldo de diamantes — incluindo ganhos, bônus, resgates e estornos.
                </p>
              </div>

              {/* 2. O que significa "Processando Resgate"? */}
              <div>
                <h3 className="font-semibold text-base mb-2">2. O que significa "Processando Resgate"?</h3>
                <p className="text-gray-700">
                  Seu pedido de resgate foi recebido e está em análise. Você será notificado(a) por WhatsApp assim que for aprovado ou reprovado.
                </p>
              </div>

              {/* 3. O que significa "Diamantes Resgatados"? */}
              <div>
                <h3 className="font-semibold text-base mb-2">3. O que significa "Diamantes Resgatados"?</h3>
                <p className="text-gray-700">
                  Indica que seu resgate foi aprovado, e você utilizou parte do seu saldo de diamantes para trocar por um item na Loja de Recompensas. O valor correspondente é descontado do seu saldo assim que o pedido de resgate é confirmado.
                </p>
              </div>

              {/* 4. O que significa "Resgate não completado"? */}
              <div>
                <h3 className="font-semibold text-base mb-2">4. O que significa "Resgate não completado"?</h3>
                <p className="text-gray-700">
                  Esse status indica que o pedido de resgate não pôde ser finalizado — seja por indisponibilidade de estoque, inconsistência nas informações ou cancelamento da solicitação. O valor correspondente em diamantes é automaticamente restituído ao seu saldo, ficando disponível para novos resgates.
                </p>
              </div>

              {/* 5. Por que aparece uma data de expiração? */}
              <div>
                <h3 className="font-semibold text-base mb-2">5. Por que aparece uma data de expiração?</h3>
                <p className="text-gray-700">
                  Todos os diamantes têm validade de 24 meses a partir da data em que foram ganhos. Após esse prazo, eles expiram automaticamente.
                </p>
              </div>

              {/* 6. O que são "Diamantes Bônus"? */}
              <div>
                <h3 className="font-semibold text-base mb-2">6. O que são "Diamantes Bônus"?</h3>
                <p className="text-gray-700">
                  São diamantes acumulados por campanhas, desafios ou indicações dentro do Programa de Rede.
                </p>
              </div>

              {/* 7. Quando os diamantes são creditados? */}
              <div>
                <h3 className="font-semibold text-base mb-2">7. Quando os diamantes são creditados?</h3>
                <ul className="space-y-2 ml-4 text-gray-700">
                  <li>• <strong>Vendas e compras:</strong> diamantes creditados automaticamente após a confirmação do pagamento pela Xizinha.</li>
                  <li>• <strong>Rede de Indicações:</strong> creditados no 1º dia de cada mês, conforme o desempenho do mês anterior e condicionado a ativação do(a) participante.</li>
                  <li>• <strong>Bônus de Ativação de Cliente:</strong> creditado quando sua indicação direta realiza a primeira ativação, ou seja, a primeira compra a partir de R$100,00.</li>
                  <li>• <strong>Campanhas e Rankings:</strong> creditados em até 10 dias úteis após o encerramento, ou conforme o prazo divulgado.</li>
                </ul>
              </div>

              {/* 8. Posso perder diamantes? */}
              <div>
                <h3 className="font-semibold text-base mb-2">8. Posso perder diamantes?</h3>
                <p className="text-gray-700">
                  Os diamantes podem ser expirados após 24 meses, ou estornados em casos de cancelamento de vendas, indicações inválidas, fraudes, ou falhas de sistema que tenham creditado valores incorretos.
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t mt-6">
              <Button
                variant="outline"
                onClick={() => setShowSaibaMaisModal(false)}
                className="flex-1"
              >
                Fechar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ExtratosDiamantes;